import React, { useState } from "react";
import type { CreatedInvitation, Tenant } from "../../types";
import { createInvitation } from "../../authApi";
import { apiErrorMessage } from "../../../utils/apiError";
import "./InviteUserForm.css";

interface Props {
  tenants: Tenant[];
  /** Only platform admins may mint other platform admins. */
  canGrantPlatformAdmin: boolean;
  onInvited: (created: CreatedInvitation) => void;
}

const InviteUserForm: React.FC<Props> = ({
  tenants,
  canGrantPlatformAdmin,
  onInvited,
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [role, setRole] = useState("member");
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await createInvitation({
        email: email.trim(),
        name: name.trim(),
        tenant_id: Number(tenantId),
        role,
        is_platform_admin: canGrantPlatformAdmin && isPlatformAdmin,
      });
      setEmail("");
      setName("");
      setTenantId("");
      setRole("member");
      setIsPlatformAdmin(false);
      onInvited(created);
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to send invite"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="admin-form invite-form" onSubmit={handleSubmit}>
      <h4>Invite User</h4>
      {error && <div className="invite-form-error">{error}</div>}
      <input
        placeholder="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <input
        placeholder="Name (optional)"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <select
        value={tenantId}
        onChange={(e) => setTenantId(e.target.value)}
        required
      >
        <option value="">Select tenant...</option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.display_name}
          </option>
        ))}
      </select>
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="member">Member</option>
        <option value="admin">Tenant Admin</option>
      </select>
      {canGrantPlatformAdmin && (
        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={isPlatformAdmin}
            onChange={(e) => setIsPlatformAdmin(e.target.checked)}
          />
          Platform admin
        </label>
      )}
      <button type="submit" disabled={submitting || !email || !tenantId}>
        {submitting ? "Sending..." : "Send Invite"}
      </button>
    </form>
  );
};

export default InviteUserForm;
